/** "trend": a trend saved from Trends, filed as it is (its title, summary, tags and lead clip). */
export type ReferenceType = "video" | "image" | "article" | "post" | "note" | "trend";
export type ReferencePlatform = "tiktok" | "instagram" | "youtube" | "web";
export type ReferenceSubmissionStatus = "queued" | "processing" | "completed" | "failed";

export interface ReferenceCarouselSlide {
  position: number;
  kind: string;
  status: string;
  /** Null when that slide could not be captured. */
  imageUrl: string | null;
}

export interface ReferenceCarousel {
  total: number;
  captured: number;
  /** Detail read only. */
  slides?: ReferenceCarouselSlide[];
}

export interface Reference {
  id: string;
  type: ReferenceType;
  platform: ReferencePlatform | null;
  /** null = global: saved for the whole workspace rather than one brand. */
  brandId: string | null;
  title: string;
  summary: string;
  /** The summary split in two by the card pass: what it is, and the move worth
   *  taking from it. Null on references saved before the split. */
  whatItIs?: string | null;
  theMove?: string | null;
  analysis: string | null;
  /** At a glance, read from the analysis; empty or null where it says nothing. */
  palette?: string[];
  hook?: string | null;
  mood?: string[];
  shot?: string | null;
  tags: string[];
  /** The why, in a person's words. */
  userNote: string | null;
  userNoteUpdatedAt?: string | null;
  /** Who last wrote the why; null when it came with the save (list rows only). */
  userNoteBy?: { id: string; name: string | null } | null;
  /** M7's drafted why, waiting on Accept or Dismiss. Never sent to a Guest. */
  suggestedNote?: string | null;
  /** The submission that produced it. */
  submissionId?: string | null;
  /** Instagram carousels: the slide count, and every slide on the detail read. */
  carousel?: ReferenceCarousel | null;
  sourceUrl: string | null;
  resolvedUrl: string | null;
  authorHandle: string | null;
  postedAt: string | null;
  caption: string | null;
  metrics: { views?: number; likes?: number; comments?: number; durationMs?: number } | null;
  thumbnailUrl: string | null;
  /** The trend it was saved from; null once that trend is gone, and on everything else. */
  trendId?: string | null;
  /** Presigned per response — never cache these, and never poll a query that returns them. */
  videoUrl: string | null;
  imageUrl: string | null;
  createdByUserId: string;
  /** UserRole when it was added: "Guest" = added by the brand's client. Null = the team. */
  createdByRole?: string | null;
  /** Who added it (list responses only). */
  createdBy?: { id: string; name: string | null; role: string | null };
  createdAt: string;
  /** The collections it sits in, of those the viewer can see. */
  collectionIds?: string[];
}

/** One picture on a collection's cover: presigned per response, like a card's. */
export interface ReferenceCollectionCover {
  referenceId: string;
  videoUrl: string | null;
  imageUrl: string | null;
}

/** A named set of references, for one brand or for Everyone (brandId null). */
export interface ReferenceCollection {
  id: string;
  name: string;
  brandId: string | null;
  /** References in it that the viewer can open. */
  count: number;
  /** Up to three of its newest references with media. */
  cover: ReferenceCollectionCover[];
  createdBy: { id: string; name: string | null; role: string | null };
  createdAt: string;
  updatedAt: string;
}

export interface ReferenceCollectionsResponse {
  collections: ReferenceCollection[];
}

/** True when a brand's client (a Guest) added this reference. */
export function addedByClient(reference: Pick<Reference, "createdByRole">): boolean {
  return reference.createdByRole === "Guest";
}

export interface ReferenceListResponse {
  references: Reference[];
  total: number;
  page: number;
}

/** One item that produced no reference. */
export interface ReferenceSkip {
  input: string;
  reason: string;
  existingReferenceId?: string;
}

export interface ReferenceSubmission {
  id: string;
  status: ReferenceSubmissionStatus;
  rawInput: string;
  /** Files saved with it, by name. */
  uploads?: { fileName: string; mimeType: string }[];
  /** The trend it was saved from, when it came from Trends. */
  trendId?: string | null;
  brandId: string | null;
  error: string | null;
  skipped: ReferenceSkip[];
  createdByUserId: string;
  createdByRole?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReferenceSubmissionsResponse {
  submissions: ReferenceSubmission[];
}
